import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import MainLayout from 'core/layout/MainLayout';
import { CVHttpService } from '../services';
import { HttpService } from 'core/services/http';
import { useForm } from 'react-hook-form';
import {
    Box,
    Button,
    Paper,
    Stepper,
    Step,
    StepLabel,
    Typography,
    TextField,
    Stack,
    Alert,
    CircularProgress
} from '@mui/material';
import PersonalInfoStep from '../components/steps/PersonalInfoStep';
import ExperiencesStep from '../components/steps/ExperiencesStep';
import EducationStep from '../components/steps/EducationStep';
import SkillsStep from '../components/steps/SkillsStep';
import LanguagesStep from '../components/steps/LanguagesStep';
import CertificatesStep from '../components/steps/CertificatesStep';
import ReferencesStep from '../components/steps/ReferencesStep';
import PreviewStep from '../components/steps/PreviewStep';

const steps = [
    'Personal Information',
    'Work Experience',
    'Education',
    'Skills',
    'Languages',
    'Certificates',
    'References',
    'Preview & Finish'
];

function CVBuilderPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [activeStep, setActiveStep] = useState(0);
    const [cvData, setCvData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [saving, setSaving] = useState(false);

    const { register, handleSubmit, formState: { errors }, watch, setValue, reset, control } = useForm({
        defaultValues: cvData || {
            title: '',
            name: '',
            last_name: '',
            email: '',
            phone: '',
            website: '',
            city: '',
            address: '',
            description: '',
            interests: '',
            country: ''
        }
    });

    React.useEffect(() => {
        const token = HttpService.getToken();
        const user = HttpService.getUser();
        
        if (!token || !user) {
            navigate('/cv');
            return;
        }

        if (id) {
            loadCV(id);
        }
    }, [id, navigate]);

    const loadCV = async (cvId) => {
        setLoading(true);
        try {
            const data = await CVHttpService.loadCV(cvId);
            
            // Check if this is an AI-generated CV (markdown format)
            const isAIGenerated = data.description && (
                data.description.trim().startsWith('#') || 
                data.description.includes('##') ||
                (data.description.includes('---') && data.description.split('\n').length > 10)
            );
            
            if (isAIGenerated) {
                // Redirect AI-generated CVs to the markdown editor
                navigate(`/cv/ai-editor/${cvId}`);
                return;
            }
            
            setCvData(data);
            reset(data);
        } catch (err) {
            setError('Failed to load CV');
        } finally {
            setLoading(false);
        }
    };

    const handleNext = async (formData = null) => {
        if (activeStep === 0) {
            // First step: save basic CV info
            if (!formData) return;
            
            setSaving(true);
            setError('');
            try {
                const body = {
                    id: cvData?.id || null,
                    title: formData.title || `${formData.name} ${formData.last_name}`.trim() || 'My CV',
                    name: formData.name,
                    last_name: formData.last_name,
                    email: formData.email,
                    phone: formData.phone,
                    website: formData.website,
                    city: formData.city,
                    address: formData.address,
                    description: formData.description,
                    interests: formData.interests,
                    country: formData.country
                };

                const savedCV = await CVHttpService.saveCV(body);
                setCvData(savedCV);
                
                if (!id && savedCV.id) {
                    navigate(`/cv/builder/${savedCV.id}`, { replace: true });
                }
                
                setActiveStep((prevStep) => prevStep + 1);
            } catch (err) {
                setError('Failed to save CV. Please try again.');
            } finally {
                setSaving(false);
            }
        } else {
            setActiveStep((prevStep) => prevStep + 1);
        }
    };

    const handleBack = () => {
        setActiveStep((prevStep) => prevStep - 1);
    };

    const handleFinish = async () => {
        navigate(`/cv/${cvData.id}`);
    };

    const renderStepContent = () => {
        if (!cvData && activeStep > 0) {
            return (
                <Alert severity="warning">
                    Please complete the personal information step first.
                </Alert>
            );
        }

        switch (activeStep) {
            case 0:
                return (
                    <PersonalInfoStep
                        register={register}
                        errors={errors}
                        watch={watch}
                        setValue={setValue}
                        control={control}
                        cvData={cvData}
                    />
                );
            case 1:
                return <ExperiencesStep cvId={cvData?.id} />;
            case 2:
                return <EducationStep cvId={cvData?.id} />;
            case 3:
                return <SkillsStep cvId={cvData?.id} />;
            case 4:
                return <LanguagesStep cvId={cvData?.id} />;
            case 5:
                return <CertificatesStep cvId={cvData?.id} />;
            case 6:
                return <ReferencesStep cvId={cvData?.id} />;
            case 7:
                return <PreviewStep cvId={cvData?.id} onFinish={handleFinish} />;
            default:
                return null;
        }
    };

    if (loading) {
        return (
            <MainLayout>
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
                    <CircularProgress />
                </Box>
            </MainLayout>
        );
    }

    return (
        <MainLayout>
            <section className="section-box mt-80">
                <div className="container">
                    <Paper elevation={3} sx={{ p: 4, borderRadius: 3 }}>
                        <Typography variant="h4" fontWeight={700} gutterBottom>
                            {cvData ? 'Edit CV' : 'Create New CV'}
                        </Typography>
                        
                        <Stepper activeStep={activeStep} sx={{ mt: 4, mb: 4 }}>
                            {steps.map((label) => (
                                <Step key={label}>
                                    <StepLabel>{label}</StepLabel>
                                </Step>
                            ))}
                        </Stepper>

                        {error && (
                            <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
                                {error}
                            </Alert>
                        )}

                        <Box sx={{ mt: 4, mb: 4 }}>
                            {renderStepContent()}
                        </Box>

                        <Stack direction="row" spacing={2} justifyContent="space-between" sx={{ mt: 4 }}>
                            <Button
                                disabled={activeStep === 0}
                                onClick={handleBack}
                                variant="outlined"
                            >
                                Back
                            </Button>
                            
                            <Box>
                                {activeStep === 0 && (
                                    <Button
                                        variant="contained"
                                        onClick={handleSubmit(handleNext)}
                                        disabled={saving}
                                    >
                                        {saving ? 'Saving...' : 'Next: Add Experience'}
                                    </Button>
                                )}
                                
                                {activeStep > 0 && activeStep < steps.length - 1 && (
                                    <Button
                                        variant="contained"
                                        onClick={handleNext}
                                    >
                                        Next
                                    </Button>
                                )}
                                
                                {activeStep === steps.length - 1 && (
                                    <Button
                                        variant="contained"
                                        onClick={handleFinish}
                                        color="success"
                                    >
                                        Finish & View CV
                                    </Button>
                                )}
                            </Box>
                        </Stack>
                    </Paper>
                </div>
            </section>
        </MainLayout>
    );
}

export default CVBuilderPage;

